import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Button, makeStyles } from '@fluentui/react-components';
import { EditRegular } from '@fluentui/react-icons';
import StackColumn from '../../common/StackColumn';
import Card from '../../common/card/Card';
import ShimmeredDetailsListWrapper from '../../common/DetailsLists/ShimmeredDetailsListWrapper';
import CanAccess from '../../common/canAccess/CanAccess';
import getColumns from './EntityTerminationBlockerPage.config';
import {
  GET_BLOCKER_QUEUE_REVIEW,
  APPROVE_ENTITY_TERMINATION_BLOCKER_REVIEW,
  CREATE_ENTITY_TERMINATION_BLOCKER_REVIEW_COMMENT,
  ATTACH_ENTITY_TERMINATION_BLOCKER_REVIEW_DOCUMENT,
  UPDATE_ENTITY_TERMINATION_BLOCKER_DOCUMENTS,
} from '../../../utils/statApi/EntityTerminationApi';
import {
  bodyContentContainer,
  iconButtonStyles,
} from '../../../app/common/styles/CommonStyleObjects';
import { GET_COMPANY_OTHER_DOCUMENTS } from '../../../utils/statApi/CompaniesApi';
import { IDocument } from '../../../utils/statApi/DocumentApi';
import DocumentView from '../../common/documents/DocumentView';
import DocumentsPanel from '../../common/documents/DocumentsPanel';
import FeatureFlagged from '../../common/featureFlagged/featureFlagged';
import EntityTerminationBlockerEditPanel from './EntityTerminationBlockerEditPanel';
import ReviewComponent from '../../reviews/ReviewComponent';
import BlockerReviewApprovalPanel from './BlockerReviewApprovalPanel';
import { IEntityTerminationActionItem } from '../types';

const useStyles = makeStyles({
  bottomSpacing: { marginBottom: '50px' },
});

interface IEntityTerminationBlockerPageProps {
  loading?: boolean;
  companyCode: string;
}

const EntityTerminationBlockerPage = (props: IEntityTerminationBlockerPageProps): JSX.Element => {
  const { loading, companyCode } = props;
  const classes = useStyles();
  const companyCodeId = parseInt(companyCode, 10);

  const blockerQueueReview = useQuery(GET_BLOCKER_QUEUE_REVIEW, {
    variables: {
      companyCode,
    },
    fetchPolicy: 'cache-and-network',
  });

  const blockerQueueData = blockerQueueReview?.data;

  const {
    data: documentsData,
    loading: documentsLoading,
    refetch: refetchDocuments,
  } = useQuery(GET_COMPANY_OTHER_DOCUMENTS, {
    variables: {
      parentId: companyCodeId,
      showDeletedFiles: false,
    },
    fetchPolicy: 'network-only',
  });

  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const [isReviewApprovalPanelOpen, setIsReviewApprovalPanelOpen] = useState<boolean>(false);
  const [isDocumentPanelOpen, setIsDocumentPanelOpen] = useState<boolean>(false);

  const terminationDocuments = documentsData?.otherCompanyDocuments?.terminationDocuments || [];
  const visibleTerminationDocuments = terminationDocuments.filter(
    (doc: IDocument) => !doc.isDeleted,
  );

  const blockerActionItems: IEntityTerminationActionItem[] =
    blockerQueueData?.blockerQueueReview?.blockerQueueActionItems || [];

  const allBlockersCompleted = blockerActionItems.every(
    (item: IEntityTerminationActionItem) =>
      item.dateCompleted !== null &&
      item.dateCompleted !== undefined &&
      (item.tasks || []).every((task) => task.completedDate !== null && task.completedDate),
  );

  const onDocumentPanelClose = () => {
    refetchDocuments();
    setIsDocumentPanelOpen(false);
  };

  const renderDocumentsActionButton = (): JSX.Element => {
    return (
      <CanAccess requestedAction="EntityTerminationDocuments:Upload">
        <Button
          appearance="subtle"
          title="Upload Documents"
          aria-label="Upload Documents"
          icon={<EditRegular color={iconButtonStyles.color} />}
          onClick={(): void => setIsDocumentPanelOpen(true)}
        />
      </CanAccess>
    );
  };

  const newBlockerButton = (): JSX.Element => {
    return (
      <CanAccess requestedAction="BlockerQueue:EditActionItems">
        <Button
          appearance="primary"
          onClick={() => {
            setIsPanelOpen(true);
          }}
        >
          Add New Blocker
        </Button>
      </CanAccess>
    );
  };

  const approveDisabledTooltip = !allBlockersCompleted
    ? 'All blockers and blocker tasks must be completed before this review can be approved.'
    : undefined;

  return (
    <FeatureFlagged flagName="EntityTerminationBlocker">
      <div className={`${bodyContentContainer} lightBackground`}>
        <Stack>
          <Stack
            horizontal
            wrap
            horizontalAlign="space-between"
            tokens={{ childrenGap: 20 }}
            className={classes.bottomSpacing}
          >
            <StackColumn>
              <Card
                title="Documents"
                isLoading={!!loading || documentsLoading}
                headingLevel={2}
                actionButton={renderDocumentsActionButton()}
              >
                {!documentsLoading && visibleTerminationDocuments.length > 0 ? (
                  <Stack horizontal wrap tokens={{ childrenGap: 20 }}>
                    {visibleTerminationDocuments.map((document: IDocument, index: number) => (
                      <DocumentView key={document.id || index} document={document} index={index} />
                    ))}
                  </Stack>
                ) : (
                  !documentsLoading && <p>No Documents Uploaded</p>
                )}
              </Card>
            </StackColumn>
          </Stack>

          <Stack>
            <Card
              title="Blockers"
              isLoading={!!loading}
              headingLevel={2}
              actionButton={newBlockerButton()}
            >
              <ShimmeredDetailsListWrapper
                columns={getColumns(companyCode)}
                items={blockerQueueData?.blockerQueueReview?.blockerQueueActionItems || []}
                singularListLabel="Blocker"
                pluralListLabel="Blockers"
              />
            </Card>
          </Stack>

          <Stack>
            <ReviewComponent
              review={blockerQueueData?.blockerQueueReview?.review}
              approveMutation={APPROVE_ENTITY_TERMINATION_BLOCKER_REVIEW}
              loading={blockerQueueReview.loading}
              createCommentMutation={CREATE_ENTITY_TERMINATION_BLOCKER_REVIEW_COMMENT}
              attachDocumentMutation={ATTACH_ENTITY_TERMINATION_BLOCKER_REVIEW_DOCUMENT}
              documentAccessPolicy="EntityTermination:ReviewContribute"
              commentAccessPolicy="EntityTermination:ReviewContribute"
              onApprovalOverrideMethod={() => setIsReviewApprovalPanelOpen(true)}
              displayOnly={!blockerQueueData?.blockerQueueReview?.hasActiveBlockerReview}
              customActionButtons={[
                {
                  label: 'Approve',
                  accessPolicy: 'EntityTerminationBlockerReview:Approval',
                  appearance: 'primary',
                  disabled: !allBlockersCompleted,
                  tooltip: approveDisabledTooltip,
                  onClick: () => setIsReviewApprovalPanelOpen(true),
                },
              ]}
            />
            {isReviewApprovalPanelOpen && (
              <BlockerReviewApprovalPanel
                closePanel={() => setIsReviewApprovalPanelOpen(false)}
                data={blockerQueueData?.blockerQueueReview}
                blockerItems={blockerQueueData?.blockerQueueReview?.blockerQueueActionItems || []}
              />
            )}
          </Stack>
        </Stack>

        {isPanelOpen && (
          <EntityTerminationBlockerEditPanel
            closePanel={() => setIsPanelOpen(false)}
            isNewBlocker
            companyCode={companyCode}
            existingBlockers={blockerQueueData?.blockerQueueReview?.blockerQueueActionItems || []}
            refetchQueries={[
              {
                query: GET_BLOCKER_QUEUE_REVIEW,
                variables: { companyCode },
              },
            ]}
          />
        )}
        {isDocumentPanelOpen && (
          <DocumentsPanel
            closePanel={onDocumentPanelClose}
            parentId={companyCodeId}
            documentType="Termination"
            initialDocuments={documentsData?.otherCompanyDocuments?.terminationDocuments || []}
            mutation={UPDATE_ENTITY_TERMINATION_BLOCKER_DOCUMENTS}
            showDeletedFiles={false}
            documentTypeIsServerDefined
          />
        )}
      </div>
    </FeatureFlagged>
  );
};

export default EntityTerminationBlockerPage;
